import { useMutation, useQuery } from 'react-query';
import axios from 'utilities/axios';
import { messageSuccess } from 'utilities/message';
import queryClient from 'utilities/queryClient';
import * as routePath from 'routers/route-path';
import moment from 'moment';
import Cookies from 'js-cookie';
import { TOKEN } from 'constants/cookies';
import { getFullName } from 'utilities/stringHelper';
import domainManagementService from 'services/domainManagementService';
import { useIntl } from 'react-intl';
const hostname = domainManagementService.getHostnameFromBrandKey();

const API = {
  GET_LOGGED_PROFILE: 'users/profile',
  GET_LOGGED_ACCOUNT: 'users/account',
  GET_LOGGED_PERSONAL: 'users/personal',
  CHANGE_PASSWORD: (id) => `users/${id}/change-password`,
  CHANGE_LANGUAGE: `users/profile/language`,
  SWITCH_ORG: 'users/switch-org',
  JOIN_ORG: 'users/accept-invite',
};

const enabledFetch = ![
  routePath.LOGIN,
  routePath.SIGNUP,
  routePath.FORGOT_PASSWORD,
  routePath.ESTIMATOR,
  '/me/set-pass',
  '/shared/analytics',
].some((el) => window.location.pathname.indexOf(el) > -1);

export default function useProfile() {
  return useQuery(
    'profile',
    () => axios.get(API.GET_LOGGED_PROFILE).then((res) => res.data),
    {
      enabled: enabledFetch,
      onError: () => {
        multipleAccount.signOutAccount(null, Cookies.get(TOKEN));
      },
      onSuccess: (data) => {
        multipleAccount.updateAccount(data);
      },
    }
  );
}

export const useUpdateProfile = (showSuccessMsg = true) => {
  const intl = useIntl();
  return useMutation((values) => axios.put(API.GET_LOGGED_PROFILE, values), {
    onSuccess: (data, values) => {
      if (showSuccessMsg) {
        messageSuccess({
          key: 'updateProfile',
          content: intl.formatMessage({
            id: 'profile.update_success',
            defaultMessage: "You've updated your profile successfully!",
          }),
        });
      }
      queryClient.setQueryData('profile', values);
      queryClient.invalidateQueries('profile');
    },
  });
};

export const useUpdateAccountInfo = (showSuccessMsg = true) => {
  const intl = useIntl();
  return useMutation((values) => axios.put(API.GET_LOGGED_ACCOUNT, values), {
    onSuccess: (data, values) => {
      if (showSuccessMsg) {
        messageSuccess({
          key: 'updateAccountInfo',
          content: intl.formatMessage({
            id: 'profile.update_account_success',
            defaultMessage: "You've updated your account information successfully!",
          }),
        });
      }
      queryClient.setQueryData('profile', values);
      queryClient.invalidateQueries('profile');
    },
  });
};

export const useUpdatePersonalInfo = (showSuccessMsg = true) => {
  const intl = useIntl();
  return useMutation((values) => axios.put(API.GET_LOGGED_PERSONAL, values), {
    onSuccess: (data, values) => {
      if (showSuccessMsg) {
        messageSuccess({
          key: 'updatePersonalInfo',
          content: intl.formatMessage({
            id: 'profile.update_personal_success',
            defaultMessage: "You've updated your personal information successfully!",
          }),
        });
      }
      queryClient.setQueryData('profile', values);
      queryClient.invalidateQueries('profile');
    },
  });
};

export const useSwitchOrg = () => {
  const intl = useIntl();
  return useMutation((values) => axios.post(API.SWITCH_ORG, values), {
    onSuccess: ({ data }, values) => {
      messageSuccess({
        key: 'switch-org',
        content: intl.formatMessage({
          id: 'profile.switch_org_success',
          defaultMessage: "You've switched organization successfully!",
        }),
      });

      Cookies.set(TOKEN, data.accessToken);
      Cookies.set(TOKEN, data.accessToken, {
        domain: `.${hostname}`,
      });
      queryClient.invalidateQueries();
    },
  });
};

export const acceptJoinOrg = () => {
  return useMutation((values) =>
    axios.post(API.JOIN_ORG, values).then((res) => res.data)
  );
};

export const useChangePassword = () => {
  return useMutation(
    (values) => axios.put(API.CHANGE_PASSWORD(values.id), values),
    {
      onError: () => {},
    }
  );
};

export const useChangeLanguage = () => {
  return useMutation((values) => axios.put(API.CHANGE_LANGUAGE, values), {
    onError: () => {},
  });
};

// account = {
//   token,
//   expiredTime, // often 30 days after login method
//   id,
//   name,
// }
export const multipleAccount = {
  init: function () {
    const accounts = this.getAccounts();
    if (Array.isArray(accounts) && accounts.length > 0) {
      accounts.filter((acc) =>
        moment(acc.expiredTime).isBefore(moment().startOf('day'))
      );
    }
    this.setAccounts(accounts);
  },
  getAccounts: function () {
    return JSON.parse(localStorage.getItem('accounts') || '[]');
  },
  setAccounts: function (accounts) {
    localStorage.setItem('accounts', JSON.stringify(accounts));
  },
  updateAccount: function (user) {
    const token = Cookies.get(TOKEN);
    let accounts = this.getAccounts();
    const index = accounts.findIndex(
      (el) => el.token === token || el.id === user.id
    );
    const expiredTime = moment().add(30, 'days');
    if (index < 0) {
      accounts.push({
        token,
        id: user.id,
        name: getFullName(user.firstName, user.lastName),
        email: user.email,
        expiredTime,
      });
    } else {
      accounts[index] = {
        ...accounts[index],
        token,
        expiredTime:
          token === accounts[index].token
            ? accounts[index].expiredTime
            : expiredTime,
        name: getFullName(user.firstName, user.lastName),
        email: user.email,
      };
    }
    this.setAccounts(accounts);
  },
  signOutAccount: function (id, token) {
    if (!id && !token) return;
    const accounts = this.getAccounts()
      .filter((el) => el.id !== id)
      .filter((el) => el.token !== token);
    this.setAccounts(accounts);
  },
  signOutAllAccount: function () {
    this.setAccounts([]);
  },
};

multipleAccount.init();
